import type { BaseRow } from '@openx/types/tableHelpers.ts';
import { STICKY_POSITION } from '@openx/types/tableHelpers.ts';
import { useTranslation } from 'react-i18next';
import { NameCell } from '../../../../DealInsightsGeneral/sections/GeneralDataTable/parts/NameCell.tsx';
import { DEAL_INSIGHTS_COLUMN_MIN_WIDTH } from '../../../../HeatTable/constants.ts';
import { useHeatmapTableColumns } from '../../../../HeatTable/hooks/useHeatmapTableColumns.tsx';
import type { ColorConfig, ColumnConfig } from '../../../../HeatTable/types.ts';

export type DealInsightsData = BaseRow & {
  avg_frequency: string;
  avg_frequency_raw: number | null;
  impressions: string;
  impressions_raw: number | null;
  uniques: string;
  uniques_raw: number | null;
  viewability: string;
  viewability_raw: number | null;
  deal_name: string;
  deal_id: string;
  ox_deal_id: number;
  spend: string;
  spend_raw: number | null;
};

export type MaxValuesConfig = {
  avg_frequency: ColorConfig<DealInsightsData>;
  impressions: ColorConfig<DealInsightsData>;
  uniques: ColorConfig<DealInsightsData>;
  viewability: ColorConfig<DealInsightsData>;
  spend: ColorConfig<DealInsightsData>;
};

export const useAudiencesTableColumns = (maxValues: MaxValuesConfig) => {
  const { t } = useTranslation();

  const columnsConfig: ColumnConfig<DealInsightsData>[] = [
    {
      key: 'deal_name',
      title: t('Deal Name'),
      width: '30%',
      minWidth: DEAL_INSIGHTS_COLUMN_MIN_WIDTH.DEAL_NAME,
      sticky: STICKY_POSITION.LEFT,
      disabled: true,
      freeze: true,
      render: ({ deal_name, ox_deal_id, deal_id }) => (
        <NameCell
          id={deal_id}
          name={deal_name}
          pathTo={ox_deal_id ? `/deals/${ox_deal_id}/details` : undefined}
          subRowLabel={t('Deal ID:')}
        />
      ),
      cellStyle: {
        alignItems: 'flex-start',
        fontWeight: 'fontWeightBold',
        paddingTop: theme => theme.spacing(2),
        paddingBottom: theme => theme.spacing(2),
      },
      headerStyle: {
        paddingLeft: 4,
      },
    },
    {
      key: 'impressions',
      title: t('Impressions'),
      width: '5%',
      minWidth: DEAL_INSIGHTS_COLUMN_MIN_WIDTH.METRIC,
      colorConfig: {
        ...maxValues.impressions,
        valueKey: 'impressions_raw',
      },
      headerStyle: {
        '& > div': {
          justifyContent: 'center',
        },
      },
    },
    {
      key: 'spend',
      title: t('Spend'),
      width: '5%',
      minWidth: DEAL_INSIGHTS_COLUMN_MIN_WIDTH.METRIC,
      prefix: '$',
      colorConfig: {
        ...maxValues.spend,
        valueKey: 'spend_raw',
      },
      headerStyle: {
        '& > div': {
          justifyContent: 'center',
        },
      },
    },

    {
      key: 'uniques',
      title: t('Uniques'),
      width: '5%',
      minWidth: DEAL_INSIGHTS_COLUMN_MIN_WIDTH.METRIC,
      colorConfig: {
        ...maxValues.uniques,
        valueKey: 'uniques_raw',
      },
      headerStyle: {
        '& > div': {
          justifyContent: 'center',
        },
      },
    },
    {
      key: 'avg_frequency',
      title: t('Avg Frequency'),
      width: '5%',
      minWidth: DEAL_INSIGHTS_COLUMN_MIN_WIDTH.METRIC,
      colorConfig: {
        ...maxValues.avg_frequency,
        valueKey: 'avg_frequency_raw',
      },
      headerStyle: {
        '& > div': {
          justifyContent: 'center',
        },
      },
    },
    {
      key: 'viewability',
      title: t('Viewability'),
      width: '5%',
      minWidth: DEAL_INSIGHTS_COLUMN_MIN_WIDTH.METRIC,
      suffix: '%',
      colorConfig: {
        ...maxValues.viewability,
        valueKey: 'viewability_raw',
      },
      headerStyle: {
        '& > div': {
          justifyContent: 'center',
        },
      },
    },
  ];

  return useHeatmapTableColumns(columnsConfig);
};
